import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { IconBookmarkFilled, IconBookmarkPlus } from '@tabler/icons-react'
import { isEmpty, isNil, uniq } from 'lodash'

import { EChart } from '@/components/charts/EChart'
import { Button } from '@/components/buttons/Button'
import { toast } from '@/components/toasts/toast.helpers'
import { chartColors } from '@/utils/constants'
import { KODI_REPORTS_PATH, REPORT_VISIBILITY } from '@/views/KodiReports/kodiReports.constants'

import { pinReportChart } from '../api/kodiReportsApi'

const PIN_STATES = { IDLE: 'idle', PINNING: 'pinning', PINNED: 'pinned' }

// Reduce a date-ish string to a MM-DD key so event-marker dates match the axis's
// category labels even when the formats differ (markers come from the query as
// ISO 'YYYY-MM-DD', the QA-trend axis renders 'MM/DD'). Returns null for values
// it can't parse so they simply don't match anything.
const toDayKey = (value) => {
  if (typeof value !== 'string') {
    return null
  }
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) {
    return `${iso[2]}-${iso[3]}`
  }
  const monthDay = value.match(/^(\d{1,2})\/(\d{1,2})/)
  if (monthDay) {
    return `${monthDay[1].padStart(2, '0')}-${monthDay[2].padStart(2, '0')}`
  }
  return null
}

// Shown on the greyed-out pin for charts Kodi drew on the fly (no underlying
// query). Plain-language: Kodi Reports keeps charts current on its own, and a
// one-off chart can't do that, so it can't be pinned there.
const PIN_UNAVAILABLE_TOOLTIP =
  "Kodi Reports charts update on their own as new calls come in. This one's a one-time snapshot, so it can't be pinned."

// Shown once a chart is pinned; the button stays disabled (you can't re-pin the
// same chart) so this rides on disabledTooltip rather than the active tooltip.
const PINNED_TOOLTIP = 'Pinned to Kodi Reports ✓'

// Server-computed pin eligibility (see the backend relative_window classifier).
// A pinned chart re-runs and rolls forward, so only a chart backed by a rolling
// "last N days" window can stay current; a fixed period or a period-vs-period
// comparison is blocked.
const PIN_ELIGIBILITY = { ROLLING: 'rolling', BLOCKED: 'blocked' }

// Fallback for the greyed-out pin on a chart that can't stay current. The
// backend normally sends a more specific reason (message.pinReason); this is
// only used if it's missing.
const NOT_ROLLING_DISABLED_TOOLTIP =
  "This chart covers a fixed date range, so it can't stay current on Kodi Reports. Ask for a rolling window like 'last 30 days' to pin it."

const chartTitle = (title) => ({ text: title, left: 'center', textStyle: { fontSize: 14 } })

// Axis names sit centred along their axis (rotated on vertical axes). ECharts'
// default 'end' puts a y-axis name horizontally above the axis, in the title's
// row, and an x-axis name past the plot's right edge, where it gets clipped.
const axisName = (label) => ({ name: label || '', nameLocation: 'middle' })

// Shrink the plot so tick labels and axis names both fit inside the grid box.
// `containLabel` only reserves room for tick labels (ECharts 6 reads it as
// outerBoundsContain: 'axisLabel'), so a long axis name ran off the canvas.
const containAxisText = { outerBoundsMode: 'same', outerBoundsContain: 'all' }

// Pie: doughnut of named slices; series[0].data is [{name, value}].
const buildPieOption = ({ title, series }) => ({
  title: chartTitle(title),
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  legend: { bottom: 0, type: 'scroll' },
  color: chartColors,
  series: [
    {
      type: 'pie',
      radius: ['40%', '70%'],
      data: series[0]?.data || [],
    },
  ],
})

// Radar: one spoke per indicator, one series per entity. When the indicators
// carry no `max`, ECharts scales each spoke independently and misrepresents a
// cross-metric comparison, so derive one shared max from the data.
const buildRadarOption = ({ title, x_axis, series, indicators }) => {
  const spokes = isEmpty(indicators) ? (x_axis?.data || []).map((name) => ({ name })) : indicators
  const values = series
    .flatMap((seriesItem) => (Array.isArray(seriesItem.data) ? seriesItem.data : []))
    .filter((value) => typeof value === 'number')
  const sharedMax = isEmpty(values)
    ? undefined
    : values.reduce((max, value) => Math.max(max, value), values[0])
  const indicator = spokes.map((spoke) =>
    isNil(spoke.max) && !isNil(sharedMax) ? { ...spoke, max: sharedMax } : spoke
  )
  return {
    title: chartTitle(title),
    tooltip: { trigger: 'item' },
    legend: { bottom: 0, type: 'scroll' },
    color: chartColors,
    radar: { indicator },
    series: [
      {
        type: 'radar',
        data: series.map((seriesItem) => ({ name: seriesItem.name, value: seriesItem.data })),
      },
    ],
  }
}

// Heatmap: 2D intensity grid (e.g. call volume by day x hour, or a criteria
// co-failure matrix). x_axis.data / y_axis.data are category labels; the series
// data is [[xIndex, yIndex, value], ...]. A single-hue alpha ramp keeps it to
// one brand color rather than a new palette.
const buildHeatmapOption = ({ title, x_axis, y_axis, series, visual_map }) => {
  const heatData = series[0]?.data || []
  const values = heatData
    .map((cell) => (Array.isArray(cell) ? cell[2] : null))
    .filter((value) => typeof value === 'number')
  // reduce, not Math.max(...spread), so a large grid can't overflow the stack.
  const autoMax = isEmpty(values)
    ? 1
    : values.reduce((max, value) => Math.max(max, value), values[0])
  return {
    title: chartTitle(title),
    tooltip: { position: 'top' },
    grid: { left: 48, right: 24, top: 48, bottom: 56, ...containAxisText },
    xAxis: {
      type: 'category',
      data: x_axis?.data || [],
      ...axisName(x_axis?.label),
      splitArea: { show: true },
    },
    yAxis: {
      type: 'category',
      data: y_axis?.data || [],
      ...axisName(y_axis?.label),
      splitArea: { show: true },
    },
    visualMap: {
      min: visual_map?.min ?? 0,
      max: visual_map?.max ?? autoMax,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: [chartColors[0]], colorAlpha: [0.15, 1] },
    },
    series: [
      {
        type: 'heatmap',
        data: heatData,
        label: { show: false },
      },
    ],
  }
}

// Boxplot: distribution spread per category. Each box is the 5-number summary
// [min, Q1, median, Q3, max]; series[0].data is one such array per category.
const buildBoxplotOption = ({ title, x_axis, y_axis, series }) => ({
  title: chartTitle(title),
  tooltip: { trigger: 'item' },
  color: chartColors,
  grid: { left: 48, right: 24, top: 48, bottom: 40, ...containAxisText },
  xAxis: {
    type: 'category',
    data: x_axis?.data || [],
    ...axisName(x_axis?.label),
    boundaryGap: true,
  },
  yAxis: {
    type: 'value',
    ...axisName(y_axis?.label),
    min: y_axis?.min ?? undefined,
    max: y_axis?.max ?? undefined,
  },
  series: [
    {
      type: 'boxplot',
      data: series[0]?.data || [],
      itemStyle: { color: chartColors[0], borderColor: chartColors[1] },
    },
  ],
})

// Optional event annotations (e.g. coaching comments on a QA-score trend):
// vertical reference lines at event dates — the right way to overlay events,
// not a fake series whose y-values are timestamps. markLine on a category axis
// only renders at an exact category value, so map each marker date to the
// category for the same calendar day (formats differ: ISO vs 'MM/DD'). A marker
// on a day with no data point has no category and is dropped.
const buildMarkLine = (config, horizontal, categories) => {
  const { markers } = config
  if (isEmpty(markers?.x)) {
    return null
  }
  const categoryByDay = {}
  categories.forEach((category) => {
    const key = toDayKey(category)
    if (!isNil(key) && isNil(categoryByDay[key])) {
      categoryByDay[key] = category
    }
  })
  const markerCategories = uniq(
    markers.x
      .map((markerValue) => categoryByDay[toDayKey(markerValue)])
      .filter((category) => !isNil(category))
  )
  if (isEmpty(markerCategories)) {
    return null
  }
  const markerAxisKey = horizontal ? 'yAxis' : 'xAxis'
  return {
    symbol: 'none',
    silent: true,
    lineStyle: { type: 'dashed', color: chartColors[1] },
    label: { formatter: () => markers.label || 'Event', position: 'insideEndTop' },
    data: markerCategories.map((category) => ({ [markerAxisKey]: category })),
  }
}

// The value axis, paired with a right-hand axis when any series binds to 'right'.
const buildValueAxisPair = ({ y_axis, series }) => {
  const leftAxis = {
    type: 'value',
    ...axisName(y_axis?.label),
    min: y_axis?.min ?? undefined,
    max: y_axis?.max ?? undefined,
  }
  if (!series.some((seriesItem) => seriesItem.y_axis === 'right')) {
    return leftAxis
  }
  return [
    leftAxis,
    {
      type: 'value',
      ...axisName(series.find((seriesItem) => seriesItem.y_axis === 'right')?.y_axis_label),
      splitLine: { show: false },
    },
  ]
}

const buildCategoryAxis = (x_axis, horizontal) => ({
  type: 'category',
  data: x_axis?.data || [],
  ...axisName(x_axis?.label),
  ...(horizontal && {
    axisLabel: { width: 120, overflow: 'truncate', ellipsis: '...' },
  }),
})

const buildLegendConfig = (config) => {
  const manySeries = config.series.length > 5
  const selector = manySeries
    ? {
        selector: [
          { type: 'inverse', title: 'Deselect All' },
          { type: 'all', title: 'Select All' },
        ],
      }
    : {}
  if (config.legend === 'right') {
    return {
      type: 'scroll',
      orient: 'vertical',
      right: 0,
      top: 48,
      bottom: 20,
      textStyle: { width: 180, overflow: 'truncate', ellipsis: '...' },
      ...selector,
      ...(manySeries && { selectorPosition: 'start' }),
    }
  }
  return { bottom: 0, type: 'scroll', ...selector }
}

const buildCartesianSeries = (config, markLineConfig, horizontal) =>
  config.series.map((seriesItem, index) => {
    // 'area' is a filled line — render it as an ECharts line with areaStyle
    // (stacked area falls out of the existing `stacked` modifier).
    const rawType = seriesItem.chart_type || config.chart_type
    const isArea = rawType === 'area'
    const type = isArea ? 'line' : rawType
    const secondaryIndex = seriesItem.y_axis === 'right' ? 1 : 0
    return {
      name: seriesItem.name,
      type,
      data: seriesItem.data,
      smooth: type === 'line',
      symbolSize: type === 'scatter' ? 8 : 4,
      ...(isArea && { areaStyle: {} }),
      ...(horizontal
        ? { xAxisIndex: secondaryIndex, yAxisIndex: 0 }
        : { yAxisIndex: secondaryIndex, xAxisIndex: 0 }),
      itemStyle: { color: chartColors[index % chartColors.length] },
      ...(config.stacked && { stack: 'total' }),
      ...(index === 0 && markLineConfig ? { markLine: markLineConfig } : {}),
    }
  })

// bar / line / scatter / area over a category axis + value axis, with optional
// horizontal orientation, dual axis, stacking, event markers, and legend.
const buildCartesianOption = (config) => {
  const { title, x_axis } = config
  const horizontal = config.orientation === 'horizontal'
  const markLineConfig = buildMarkLine(config, horizontal, x_axis?.data || [])
  const valueAxisPair = buildValueAxisPair(config)
  const categoryAxis = buildCategoryAxis(x_axis, horizontal)
  const legendRight = config.legend === 'right'
  return {
    title: chartTitle(title),
    tooltip: { trigger: 'axis' },
    legend: buildLegendConfig(config),
    color: chartColors,
    grid: {
      left: 48,
      right: legendRight ? 240 : 24,
      top: 48,
      bottom: legendRight ? 20 : 40,
      ...containAxisText,
    },
    xAxis: horizontal ? valueAxisPair : categoryAxis,
    yAxis: horizontal ? categoryAxis : valueAxisPair,
    series: buildCartesianSeries(config, markLineConfig, horizontal),
  }
}

const SPECIAL_CHART_BUILDERS = {
  pie: buildPieOption,
  radar: buildRadarOption,
  heatmap: buildHeatmapOption,
  boxplot: buildBoxplotOption,
}

export const buildEChartsOption = (config) => {
  const specialBuilder = SPECIAL_CHART_BUILDERS[config.chart_type]
  return specialBuilder ? specialBuilder(config) : buildCartesianOption(config)
}

export const KodiChartMessage = ({ message }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const readOnly = useSelector((state) => state.kodi.readOnly)
  const [pinState, setPinState] = useState(PIN_STATES.IDLE)

  const { chartOption, config } = useMemo(() => {
    try {
      const parsed = JSON.parse(message.result)

      return { chartOption: buildEChartsOption(parsed), config: parsed }
    } catch (error) {
      return { chartOption: null, config: null }
    }
  }, [message.result])

  if (!chartOption) {
    return (
      <div className="cowork-chart-message cowork-chart-message--error">
        Chart could not be rendered.
      </div>
    )
  }

  const isHorizontal = config?.orientation === 'horizontal'
  const categoryCount = (config?.x_axis?.data || []).length
  const height = isHorizontal ? Math.max(350, categoryCount * 28) : 350

  // Only query-generated charts are pinnable: they carry the balto_execute_query
  // `plan` Kodi Reports re-runs for fresh data. A render_chart chart is drawn on
  // the fly with no underlying query, so it can't be re-run — we still show the
  // pin, greyed out, with a tooltip explaining why it can't be pinned.
  const hasPlan = !isEmpty(message.plan)
  // Server-computed: whether this chart can be pinned. A chart with a plan but
  // no explicit eligibility defaults to rolling (pinnable). A fixed period or a
  // comparison is blocked — it can't roll forward to stay current.
  const eligibility = hasPlan ? message.pinEligibility || PIN_ELIGIBILITY.ROLLING : null
  const isBlocked = eligibility === PIN_ELIGIBILITY.BLOCKED
  const canPin = hasPlan && !isBlocked

  const handlePin = async () => {
    setPinState(PIN_STATES.PINNING)
    try {
      const pinned = await pinReportChart(dispatch, {
        title: config?.title || 'Untitled chart',
        plan: message.plan,
        chartIndex: message.chartIndex || 0,
      })
      setPinState(PIN_STATES.PINNED)
      toast.success('Pinned to Kodi Reports', {
        // Only a backend that pins privately says so; an older one pins org-wide.
        description:
          pinned?.visibility === REPORT_VISIBILITY.PRIVATE
            ? 'Only you can see it until you share it.'
            : undefined,
        action: { label: 'View', onClick: () => history.push(KODI_REPORTS_PATH) },
      })
    } catch (error) {
      setPinState(PIN_STATES.IDLE)
      toast.error('Could not pin chart to Kodi Reports', { cause: error })
    }
  }

  return (
    <div className="cowork-chart-message">
      {!readOnly && (
        <div className="cowork-chart-message__actions">
          <Button
            brand
            circular
            className="cowork-chart-message__pin"
            onClick={handlePin}
            loading={pinState === PIN_STATES.PINNING}
            disabled={!canPin || pinState !== PIN_STATES.IDLE}
            tooltip="Pin to Kodi Reports"
            disabledTooltip={
              isBlocked
                ? message.pinReason || NOT_ROLLING_DISABLED_TOOLTIP
                : !hasPlan
                  ? PIN_UNAVAILABLE_TOOLTIP
                  : pinState === PIN_STATES.PINNED
                    ? PINNED_TOOLTIP
                    : undefined
            }
            icon={
              pinState === PIN_STATES.PINNED ? (
                <IconBookmarkFilled aria-hidden="true" />
              ) : (
                <IconBookmarkPlus aria-hidden="true" />
              )
            }
            aria-label={
              pinState === PIN_STATES.PINNED ? 'Pinned to Kodi Reports' : 'Pin to Kodi Reports'
            }
            data-testid="kodi-chart-pin"
          />
        </div>
      )}
      <EChart option={chartOption} style={{ width: '100%', height: `${height}px` }} />
    </div>
  )
}
